import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { transactions as api } from "@/lib/api/transactions";
import type { TransactionInput } from "@/lib/api/transactions";
import type { Transaction } from "@/types/api";
import { useAuth } from "./useAuth";

export type TransactionsStatus = "idle" | "loading" | "ready" | "error";

export interface TransactionsContextValue {
  status: TransactionsStatus;
  transactions: readonly Transaction[];
  error: Error | null;
  reload: () => Promise<void>;
  create: (input: TransactionInput) => Promise<Transaction>;
  update: (id: string, patch: TransactionInput) => Promise<Transaction>;
  remove: (id: string) => Promise<void>;
  bulkImport: (rows: TransactionInput[]) => Promise<{
    inserted: number;
    duplicates: number;
    skipped: { row: number; reason: string }[];
  }>;
}

const TransactionsContext = createContext<TransactionsContextValue>(null!);

export function useTransactions(): TransactionsContextValue {
  return useContext(TransactionsContext);
}

function useTransactionsProvider(): TransactionsContextValue {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<TransactionsStatus>("idle");
  const [items, setItems] = useState<readonly Transaction[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(
    () =>
      api
        .listTransactions()
        .then(({ transactions }) => {
          setItems(transactions);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [],
  );

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    void reload();
  }, [authStatus, reload]);

  const create = useCallback(async (input: TransactionInput) => {
    const { transaction } = await api.createTransaction(input);
    setItems((prev) => [transaction, ...prev]);
    return transaction;
  }, []);

  const update = useCallback(async (id: string, patch: TransactionInput) => {
    const { transaction } = await api.updateTransaction(id, patch);
    setItems((prev) => prev.map((t) => (t.id === id ? transaction : t)));
    return transaction;
  }, []);

  const remove = useCallback(async (id: string) => {
    await api.deleteTransaction(id);
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const bulkImport = useCallback(
    async (rows: TransactionInput[]) => {
      const result = await api.bulkImportTransactions(rows);
      // The endpoint returns counts only. Refetch to surface the new
      // rows in the table without bloating the response payload.
      if (result.inserted > 0) await reload();
      return result;
    },
    [reload],
  );

  return useMemo(
    () => ({
      status,
      transactions: items,
      error,
      reload,
      create,
      update,
      remove,
      bulkImport,
    }),
    [status, items, error, reload, create, update, remove, bulkImport],
  );
}

export function TransactionsProvider({ children }: { children: ReactNode }) {
  const value = useTransactionsProvider();
  return (
    <TransactionsContext.Provider value={value}>
      {children}
    </TransactionsContext.Provider>
  );
}
